import { useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@cloudscape-design/components/box";
import Badge from "@cloudscape-design/components/badge";
import Button from "@cloudscape-design/components/button";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Link from "@cloudscape-design/components/link";
import Popover from "@cloudscape-design/components/popover";
import Modal from "@cloudscape-design/components/modal";
import { useAuth } from "../contexts/AuthContext";

function ScenarioTile({ scenario, onEdit, onDelete, onClick, currentUserSub }) {
  const { t } = useTranslation();
  const { isWriteUser } = useAuth();
  const [showFullDescription, setShowFullDescription] = useState(false);

  const isOwned = scenario.owner && scenario.owner === currentUserSub;

  const getDirectionBadgeColor = (direction) => {
    switch (direction) {
      case "inbound": return "blue";
      case "outbound": return "green";
      default: return "grey";
    }
  };

  const getVisibilityBadgeColor = (visibility) => visibility === "public" ? "blue" : "grey";
  const getStatusBadgeColor = (status) => status === "published" ? "green" : "grey";

  const getOwnerAlias = () => {
    if (scenario.owner_email) {
      return scenario.owner_email.split("@")[0];
    }
    return null;
  };

  const ownerAlias = getOwnerAlias();
  const hasOwner = Boolean(scenario.owner_email);

  return (
    <div 
      className={`scenario-tile${isOwned ? " scenario-tile-owned" : ""}`}
      onClick={() => onClick(scenario)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(scenario); }}}
      role="button"
      tabIndex={0}
      aria-label={t("scenarios.tile.select", { name: scenario.name })}
    >
      <div className="scenario-tile-image">
        {scenario.image_url ? (
          <img src={scenario.image_url} alt={scenario.name} />
        ) : (
          <div className="scenario-tile-placeholder">
            <Box textAlign="center" color="text-body-secondary">{t("scenarios.tile.no_image")}</Box>
          </div>
        )}
      </div>
      
      <div className="scenario-tile-content">
        <div className="scenario-tile-top">
          <div className="scenario-tile-header">
            <Box variant="h3" fontSize="heading-m" fontWeight="bold">{scenario.name}</Box>
            {isOwned && isWriteUser && (
              <div className="scenario-tile-actions">
                <Button variant="icon" iconName="edit" onClick={(e) => { e.stopPropagation(); onEdit(scenario); }} ariaLabel={t("scenarios.tile.edit")} />
                <Button variant="icon" iconName="remove" onClick={(e) => { e.stopPropagation(); onDelete(scenario); }} ariaLabel={t("scenarios.tile.delete")} />
              </div>
            )}
          </div>
          <div className="scenario-tile-description">
            <Box color="text-body-secondary" fontSize="body-s">
              <span className="scenario-tile-description-text">{scenario.description}</span>
            </Box>
            {scenario.description && scenario.description.length > 150 && (
              <Link
                fontSize="body-s"
                onFollow={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowFullDescription(true);
                }}
              >
                {t("scenarios.tile.read_more")}
              </Link>
            )}
          </div>
        </div>
        <div className="scenario-tile-bottom">
          <div className="scenario-tile-owner">
            <Box fontSize="body-s" color="text-body-secondary">
              {t("scenarios.tile.owner_label")}{" "}
              {hasOwner && ownerAlias ? (
                <Popover
                  dismissButton={false}
                  position="top"
                  size="small"
                  triggerType="custom"
                  content={t("scenarios.tile.open_in_phonetool")}
                >
                  <Link
                    href={`https://phonetool.amazon.com/users/${ownerAlias}`}
                    fontSize="body-s"
                    onFollow={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.open(`https://phonetool.amazon.com/users/${ownerAlias}`, "_blank", "noopener,noreferrer");
                    }}
                  >
                    @{ownerAlias}
                  </Link>
                </Popover>
              ) : (
                <Badge color="grey">{t("scenarios.ownership.community")}</Badge>
              )}
            </Box>
          </div>
          <div className="scenario-tile-footer">
            <SpaceBetween direction="horizontal" size="xxs">
              <Badge color={getDirectionBadgeColor(scenario.call_direction)}>
                {t(`scenarios.call_direction.${scenario.call_direction}`)}
              </Badge>
              {scenario.visibility && (
                <Badge color={getVisibilityBadgeColor(scenario.visibility)}>
                  {t(`scenarios.visibility.${scenario.visibility}`)}
                </Badge>
              )}
              {scenario.status && (
                <Badge color={getStatusBadgeColor(scenario.status)}>
                  {t(`scenarios.status.${scenario.status}`)}
                </Badge>
              )}
            </SpaceBetween>
          </div>
        </div>
      </div>

      <Modal
        visible={showFullDescription}
        onDismiss={() => setShowFullDescription(false)}
        header={scenario.name}
        size="medium"
      >
        <Box>{scenario.description}</Box>
      </Modal>
    </div>
  );
}

export default ScenarioTile;
